import { create } from 'zustand'
import { fetchCardPage, type PagedListCard } from '../../api/generalAgentChatApi'
import type { ListPage } from './cardResults'
import type { PagedRows } from './cardPages'

/** A card's rows read in place, and whether the next page is on its way. */
interface CardPagesState extends PagedRows {
    pending: boolean
    error?: string
}

interface CardPagesStore {
    /** Keyed by the card's id: a stored card result never changes, so its extra pages stay with it. */
    byCard: Record<string, CardPagesState>
    /** Reads the card's next page in place and appends its rows. A page already on its way is not asked for again. */
    loadMore: (cardId: string, card: PagedListCard, page: ListPage) => Promise<void>
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)

const CANNOT_LOAD = 'Could not load more. Try again.'

export const useCardPagesStore = create<CardPagesStore>((set, get) => ({
    byCard: {},
    loadMore: async (cardId, card, page) => {
        const held = get().byCard[cardId]
        if (held?.pending || !page.query || page.nextPage === undefined) return
        set((state) => ({
            byCard: { ...state.byCard, [cardId]: { rows: held?.rows ?? [], nextPage: held?.nextPage ?? page.nextPage, pending: true } },
        }))
        try {
            const structured = await fetchCardPage(card, { ...page.query, page: page.nextPage })
            const rows = Array.isArray(structured.rows) ? structured.rows.filter(isRecord) : []
            const nextPage = typeof structured.next_page === 'number' ? structured.next_page : undefined
            set((state) => ({
                byCard: {
                    ...state.byCard,
                    [cardId]: { rows: [...(state.byCard[cardId]?.rows ?? []), ...rows], nextPage, pending: false },
                },
            }))
        } catch {
            set((state) => ({
                byCard: {
                    ...state.byCard,
                    [cardId]: { rows: state.byCard[cardId]?.rows ?? [], nextPage: state.byCard[cardId]?.nextPage, pending: false, error: CANNOT_LOAD },
                },
            }))
        }
    },
}))
